// Pure planner for "switch template, keep what still fits". Given the draft the marketer last saved, the template
// it was saved on and the template they picked next, it says which saved values can prefill that template and
// which slots stay blank. The draft says what it holds, the way restore's drift check reads it: its messageType
// gives the header kind, buttonUrlParam / ctaButtonUrl the dynamic button, the lengths of headerParams / params
// the variable slots, waTemplateJson the buttons, cards or offer. The template it was saved on comes in too
// (fromTemplate), because the wire JSON writes a link suffix and a tracked link both as a 'url' component, so
// only the templates can say which one a saved value is.
// No React and no app imports (see ../README.md): it reads the same raw list-row shape describeTemplate()
// reads, so it can move into @gs/gs-integration-component as is.
//
// A value only ever lands in a slot of the SAME KIND: a media URL in the same media header, a button
// suffix in a suffix field, a tracked link in a tracked-link field. Positional variables carry only
// when the count matches, since there is no honest partial mapping. COMPLEX / LTO / CAROUSEL wire
// JSON carries only when the whole template lines up, because its shape is the template's shape, and only when
// it holds something the marketer typed: a form nobody filled in carries nothing and reports nothing.
//
// Total on purpose: nothing here throws, mutates its input, or hands back a reference into it.
// Anything missing or malformed means "do not carry that slot".
import {
  describeTemplate, DYNAMIC_BUTTON_FIELDS, MESSAGE_TYPE_BY_HEADER_KIND, SEND_MEDIA_KINDS, buttonInputKind, buttonsTravelAsJson,
} from './templateModel';
import {
  cardsMatch, carouselShapeSignature, cloneJson, isObject, ltoExpiryMatches, wireJsonHoldsValue,
} from './savedJsonMatch';
import { isFilled } from './validators';

/**
 * @typedef {object} CarryPrefill
 * @property {string[]} headerParams - one per header variable. A slot nothing fills is ''; the shared
 *   component's own "{{n}}" placeholder for it is the host's to add (see pages/Message toRestoreView)
 * @property {string[]} params - one per body variable, same rule
 * @property {string|null} mediaUrl
 * @property {{ latitude: string, longitude: string, name: string, address: string }|null} location
 * @property {string|null} buttonUrlParam - a single DYNAMIC button's suffix
 * @property {string|null} ctaButtonUrl - a single DYNAMIC_LINK_TRACK button's full URL
 * @property {object|null} waTemplateJson - COMPLEX / LTO / CAROUSEL wire JSON, always a deep copy
 */

const MEDIA_LABEL = {
  IMAGE: 'header image', VIDEO: 'header video', DOCUMENT: 'header document', GIF: 'header GIF',
};

const asList = (value) => (Array.isArray(value) ? value : []);

// Only a non-blank string counts as filled; every other slot is ''.
const fill = (values) => values.map((value) => (isFilled(value) ? value : ''));

function blankFor(described) {
  return {
    headerParams: Array.from({ length: described.headerVarCount }, () => ''),
    params: Array.from({ length: described.bodyVarCount }, () => ''),
    mediaUrl: null,
    location: null,
    buttonUrlParam: null,
    ctaButtonUrl: null,
    waTemplateJson: null,
  };
}

function copyLocation(value) {
  if (!isObject(value)) return null;
  const field = (key) => (typeof value[key] === 'string' ? value[key] : '');
  const location = {
    latitude: field('latitude'), longitude: field('longitude'), name: field('name'), address: field('address'),
  };
  return location.latitude.trim() && location.longitude.trim() ? location : null;
}

// The buttons a COMPLEX / LTO / voice-call send writes a component for: what the Standard panel's
// buildButtonComponents emits, and what the library's COMPLEX form collects. The wire cannot tell a
// suffix from a tracked link (both are a 'url' component), so those two compare equal here; the templates
// tell them apart (valueButtonKinds, sameCardKinds).
const takesComponent = (button) => buttonInputKind(button) !== null || button.type === 'VOICE_CALL';
// "index:kind", kind lowercase: the Standard panel writes sub_type 'copy_code', the library 'COPY_CODE'.
const buttonKey = (index, kind) => `${index}:${String(kind).toLowerCase()}`;

// Same buttons at the same indexes, and nothing in the JSON but buttons and the offer's expiry.
function buttonJsonFits(b, details, json) {
  const components = json.components;
  if (!components.every((component) => component?.type === 'button' || component?.type === 'limited_time_offer')) return false;
  const saved = components.filter((component) => component?.type === 'button').map((component) => buttonKey(component.index, component.sub_type));
  const wanted = b.buttons.filter(takesComponent).map(({ index, type }) => buttonKey(index, type));
  return saved.length === wanted.length && wanted.every((key) => saved.includes(key)) && ltoExpiryMatches(details, json);
}

// "index:kind" of every button that takes a value, sorted: equal lists mean the same kinds at the same indexes.
const valueButtonKinds = (described) => described.buttons
  .filter((button) => buttonInputKind(button) !== null)
  .map((button) => `${button.index}:${buttonInputKind(button)}`)
  .sort()
  .join();

// Card for card, the same media, body variables, dynamic buttons and kinds of dynamic button.
const sameCardKinds = (fromDetails, toDetails) => JSON.stringify(carouselShapeSignature(fromDetails.carouselCards))
  === JSON.stringify(carouselShapeSignature(toDetails.carouselCards));

// The deep copy of the saved wire JSON when it fits B whole, else null. The cards and the offer's expiry are
// judged by the same check restore uses to spot drift. A suffix and a tracked link look alike on the wire, so
// the JSON also needs the template it was saved on, with the same kinds of button where B has them.
function carryWireJson(b, details, saved, sameHeader, fromTemplate) {
  const json = saved.waTemplateJson;
  const isCarousel = b.buttonType === 'CAROUSEL';
  if (!buttonsTravelAsJson(b) || !isObject(json) || !Array.isArray(json.components) || json.components.length === 0) return null;
  if (!isObject(fromTemplate?.details)) return null;

  const sameShape = sameHeader
    && asList(saved.headerParams).length === b.headerVarCount
    && asList(saved.params).length === b.bodyVarCount;
  if (!sameShape) return null;

  const fromDetails = fromTemplate.details;
  const fits = isCarousel
    ? cardsMatch(details.carouselCards, json) && sameCardKinds(fromDetails, details)
    : buttonJsonFits(b, details, json) && valueButtonKinds(describeTemplate(fromDetails)) === valueButtonKinds(b);
  if (!fits) return null;
  // Copy first: a cycle or BigInt means "not carriable", and the copy is plain data the value check can walk safely.
  const copy = cloneJson(json);
  return copy !== undefined && wireJsonHoldsValue(copy) ? copy : null;
}

/**
 * Plans what a saved message can hand to a different template.
 * @param {object} [input]
 * @param {object} input.saved - the saved draft: channelId, templateId, messageType, headerParams, params,
 *   mediaUrl, location, buttonUrlParam, ctaButtonUrl, waTemplateJson. Anything else on it is ignored.
 * @param {{ details?: object }} input.toTemplate - the template picked next, a mapTemplate() row
 * @param {string} input.channelId - the channel that template was picked on
 * @param {{ details?: object }} [input.fromTemplate] - the template the draft was saved on, its current
 *   mapTemplate() row; undefined when it is no longer listed. Without it, wire JSON carries nothing: there is no
 *   telling which kind of link button a 'url' component stands for.
 * @returns {{ carried: CarryPrefill, kept: string[] }|null} null when there is no draft, it was saved on
 *   this template or on another channel, or nothing of it fits. `kept` names what carried, in screen
 *   order, for the notice: header image|video|document|GIF, location, header text, "<n> variable(s)",
 *   button link, tracked link, buttons, carousel cards, offer.
 */
export function planCarry(input) {
  const {
    saved, toTemplate, channelId, fromTemplate,
  } = input ?? {};
  if (!isObject(saved) || !isObject(toTemplate?.details)) return null;
  if (channelId !== saved.channelId || String(toTemplate.details.id) === String(saved.templateId)) return null;

  const details = toTemplate.details;
  const b = describeTemplate(details);
  const headerParams = asList(saved.headerParams);
  const params = asList(saved.params);
  // messageType is the header kind: 'text' stands for a text header or none, which the header slots tell apart.
  const sameHeader = saved.messageType === MESSAGE_TYPE_BY_HEADER_KIND[b.headerKind];

  const carried = blankFor(b);
  const kept = [];

  // A carousel's media lives on its cards; the top level has no media header of its own.
  if (sameHeader && b.buttonType !== 'CAROUSEL' && SEND_MEDIA_KINDS.includes(b.headerKind) && isFilled(saved.mediaUrl)) {
    carried.mediaUrl = saved.mediaUrl;
    kept.push(MEDIA_LABEL[b.headerKind]);
  }

  if (sameHeader && b.headerKind === 'LOCATION') {
    carried.location = copyLocation(saved.location);
    if (carried.location) kept.push('location');
  }

  if (b.headerVarCount > 0 && headerParams.length === b.headerVarCount) {
    carried.headerParams = fill(headerParams);
    if (carried.headerParams.some(Boolean)) kept.push('header text');
  }

  if (b.bodyVarCount > 0 && params.length === b.bodyVarCount) {
    carried.params = fill(params);
    const filled = carried.params.filter(Boolean).length;
    if (filled > 0) kept.push(`${filled} ${filled === 1 ? 'variable' : 'variables'}`);
  }

  // Suffix and full URL are different kinds of value: B takes only the one its own field holds. A template that
  // sends its buttons as JSON never reads either field (a voice-call template is built whole by the Standard
  // panel, whatever its button_type says), so a link carried there would be lost.
  const dynamic = DYNAMIC_BUTTON_FIELDS[b.buttonType];
  if (dynamic && !buttonsTravelAsJson(b) && isFilled(saved[dynamic.field])) {
    carried[dynamic.field] = saved[dynamic.field];
    kept.push(dynamic.label);
  }

  carried.waTemplateJson = carryWireJson(b, details, saved, sameHeader, fromTemplate);
  if (carried.waTemplateJson) {
    if (b.buttonType === 'CAROUSEL') kept.push('carousel cards');
    else kept.push(b.isLto ? 'offer' : 'buttons');
  }

  return kept.length === 0 ? null : { carried, kept };
}
